import _ from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {makeMapDispatchToProps, makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {CalendarVersionAccessRolesEnum} from '../../../models/api/calendarVersionModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {updateCalendarVersionAction} from '../../../store/data/calendarVersions/calendarVersionsActions';
import {
  getFindCalendarVersionName,
  getVisibleCalendarVersions,
} from '../../../store/data/calendarVersions/calendarVersionsSelectors';
import {getCalendars, getCalendarVersions} from '../../../store/data/dataSelectors';
import {getCalendarVersionIdsFromImportJobs} from '../../../store/data/jobs/jobsSelectors';
import {
  toggleSelectedCalendarVersionAction,
  unselectCalendarVersionAction,
  updateViewingNowCalendarsAction,
} from '../../../store/panels/calendars/calendarsPanelActions';
import {
  makeGetCalendarsPanel,
  makeGetSelectedCalendarVersionIds,
  makeGetViewingNowCalendars,
} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {ViewingNowCalendar} from '../../../store/panels/calendars/calendarsPanelState';
import {CalendarsPanelId} from '../../../store/panels/panelStateBase';
import {CalendarsList} from './calendarsList';

/*
 * Props.
 */

interface CalendarsListContainerProps {
  panelId: CalendarsPanelId;
  shouldSaveCalendarVersion: boolean;
}

/*
 * Selectors.
 */

function makeGetFindIsCalendarVersionImporting() {
  return createSelector(getCalendarVersionIdsFromImportJobs, (calendarVersionIdsFromImportJobs) => {
    const importingCalendarVersionIds = new Set(calendarVersionIdsFromImportJobs);
    return (id: number) => importingCalendarVersionIds.has(id);
  });
}

function makeGetFindIsCalendarVersionSelected(
  getSelectedCalendarVersionIds: ReturnType<typeof makeGetSelectedCalendarVersionIds>,
) {
  return createSelector(getSelectedCalendarVersionIds, (selectedCalendarVersionIds) => {
    const calendarVersionIds = new Set(selectedCalendarVersionIds);
    return (id: number) => calendarVersionIds.has(id);
  });
}

/**
 * Sorts the calendarVersions:
 *  1) Primary Calendars you own.
 *  2) Any selected Calendars.
 *  3) Any non selected Calendars you own.
 *  4) Any non selected Calendars you don't own.
 *  5) Alphabetical.
 */
function makeGetSortedCalendarVersions(
  getFindIsCalendarVersionSelected: ReturnType<typeof makeGetFindIsCalendarVersionSelected>,
) {
  return createSelector(
    [getCalendars, getVisibleCalendarVersions, getFindIsCalendarVersionSelected],
    (calendars, visibleCalendarVersions, findIsCalendarVersionSelected) =>
      _(visibleCalendarVersions)
        .map((calendarVersion) => {
          const calendar = calendars[calendarVersion.calendarId];
          return calendar ? {calendar, calendarVersion} : null;
        })
        .compact()
        .sortBy(({calendar, calendarVersion}) => {
          const calendarDisplayName = (calendarVersion.nameOverride || calendar.name).toLowerCase();
          const isOwner = calendarVersion.accessRole === CalendarVersionAccessRolesEnum.OWNER;

          // Invert booleans as sortBy gives false values precedence
          return [
            !(isOwner && calendarVersion.isPrimary),
            !findIsCalendarVersionSelected(calendarVersion.id),
            !isOwner,
            calendarDisplayName,
          ];
        })
        .map(({calendarVersion}) => calendarVersion)
        .value(),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CalendarsListContainerProps>()(() => {
  const getSelectedCalendarVersionIds = makeGetSelectedCalendarVersionIds();
  const getFindIsCalendarVersionImporting = makeGetFindIsCalendarVersionImporting();
  const getFindIsCalendarVersionSelected = makeGetFindIsCalendarVersionSelected(
    getSelectedCalendarVersionIds,
  );
  const getSortedCalendarVersions = makeGetSortedCalendarVersions(getFindIsCalendarVersionSelected);

  const getCalendarsPanel = makeGetCalendarsPanel();
  const getViewingNowCalendars = makeGetViewingNowCalendars(getCalendarsPanel);

  return {
    sortedCalendarVersions: getSortedCalendarVersions,
    findIsCalendarVersionImporting: getFindIsCalendarVersionImporting,
    findIsCalendarVersionSelected: getFindIsCalendarVersionSelected,
    findCalendarVersionName: getFindCalendarVersionName,
    viewingNowCalendars: getViewingNowCalendars,
    calendarVersions: getCalendarVersions,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<CalendarsListContainerProps>()(
  ({panelId, shouldSaveCalendarVersion}) => ({
    onCalendarVersionClick: (calendarVersionId: number) =>
      toggleSelectedCalendarVersionAction(panelId, calendarVersionId, shouldSaveCalendarVersion),
    onViewingNowCalendarsChange: (viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>) =>
      updateViewingNowCalendarsAction(panelId, viewingNowCalendars),

    onAddCalendarClick: (calendarVersionId: number) => {
      trackInterfaceEvent(InterfaceEventsEnum.ADD_CALENDAR_TO_LIST);
      return updateCalendarVersionAction(calendarVersionId, {isHidden: false});
    },
    onRemoveCalendarClick: (calendarVersionId: number) =>
      updateCalendarVersionAction(calendarVersionId, {isHidden: true}),
    unselectCalendarVersion: (calendarVersionId: number) =>
      unselectCalendarVersionAction(panelId, calendarVersionId, shouldSaveCalendarVersion),
  }),
);

export const CalendarsListContainer = connect(mapStateToProps, mapDispatchToProps)(CalendarsList);
